import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { DollarSign, Users, Wrench, FileDown, BarChart2, RefreshCw, Building2 } from 'lucide-react';
import { getCostReport } from '../api/reports';
import { getTools } from '../api/tools';
import { getDepartments } from '../api/employees';

const DATE_RANGES = [
  { value: 'this_month',   label: 'This Month' },
  { value: 'last_month',   label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'this_year',    label: 'This Year' },
  { value: 'last_year',    label: 'Last Year' },
  { value: 'custom',       label: 'Custom Range' },
];

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';

const SummaryCard = ({ icon: Icon, label, value, color }) => (
  <div className="card p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);

const exportCSV = (data, filters) => {
  if (!data) return;

  const rows = [];
  rows.push(['AI Tool Management — Cost Report']);
  rows.push([`Period: ${fmtDate(data.date_from)} – ${fmtDate(data.date_to)}`]);
  rows.push([]);

  rows.push(['SUMMARY']);
  rows.push(['Total Cost', 'Employees', 'Tools Used', 'Allocations']);
  rows.push([data.summary.total_cost, data.summary.total_employees, data.summary.tools_used, data.summary.total_allocations]);
  rows.push([]);

  rows.push(['COST PER AI TOOL']);
  rows.push(['Tool Name', 'Monthly Price', 'Active Users', 'Billed Periods', 'Total Cost']);
  data.per_tool.forEach((t) => rows.push([t.tool_name, t.monthly_price, t.current_active_users, t.period_allocations, t.total_cost]));
  rows.push([]);

  rows.push(['COST BY PAID BY']);
  rows.push(['Paid By', 'Employees', 'Total Cost']);
  (data.per_paid_by || []).forEach((p) => rows.push([p.paid_by, p.employee_count, p.total_cost]));
  rows.push([]);

  rows.push(['COST BY DEPARTMENT']);
  rows.push(['Department', 'Employees', 'Total Cost']);
  data.per_department.forEach((d) => rows.push([d.department, d.employee_count, d.total_cost]));

  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `cost_report_${data.date_from}_${data.date_to}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const CostReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tools, setTools] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [range, setRange] = useState('this_month');
  const [toolId, setToolId] = useState('');
  const [department, setDepartment] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => {
    Promise.all([getTools(), getDepartments()])
      .then(([t, d]) => { setTools(t.data); setDepartments(d.data); })
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    if (range === 'custom' && (!customFrom || !customTo)) {
      toast.error('Please select a custom date range.');
      return;
    }
    setLoading(true);
    try {
      const params = { range, tool_id: toolId || undefined, department: department || undefined };
      if (range === 'custom') { params.from = customFrom; params.to = customTo; }
      const res = await getCostReport(params);
      setData(res.data);
    } catch {
      toast.error('Failed to load cost report.');
    } finally {
      setLoading(false);
    }
  }, [range, toolId, department, customFrom, customTo]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const maxToolCost = data?.per_tool?.length
    ? Math.max(...data.per_tool.map((t) => Number(t.total_cost)))
    : 1;

  const maxDeptCost = data?.per_department?.length
    ? Math.max(...data.per_department.map((d) => Number(d.total_cost)))
    : 1;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cost Report</h1>
          <p className="text-sm text-gray-500 mt-1">AI tool expenditure breakdown by tool and department.</p>
        </div>
        <button
          onClick={() => exportCSV(data)}
          disabled={!data || loading}
          className="btn-secondary"
        >
          <FileDown className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Date range */}
          <div>
            <label className="label">Date Range</label>
            <select className="input w-44" value={range} onChange={(e) => setRange(e.target.value)}>
              {DATE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          {/* Custom dates */}
          {range === 'custom' && (
            <>
              <div>
                <label className="label">From</label>
                <input className="input w-36" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              </div>
              <div>
                <label className="label">To</label>
                <input className="input w-36" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
              </div>
            </>
          )}

          {/* Tool filter */}
          <div>
            <label className="label">AI Tool</label>
            <select className="input w-44" value={toolId} onChange={(e) => setToolId(e.target.value)}>
              <option value="">All Tools</option>
              {tools.filter((t) => t.is_active).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Dept filter */}
          <div>
            <label className="label">Department</label>
            <select className="input w-44" value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="">All Departments</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <button onClick={fetchReport} disabled={loading} className="btn-primary h-[38px]">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading...' : 'Apply'}
          </button>
        </div>

        {data && (
          <p className="text-xs text-gray-400 mt-3">
            Period: <span className="font-medium text-gray-600">{fmtDate(data.date_from)}</span>
            {' '}–{' '}
            <span className="font-medium text-gray-600">{fmtDate(data.date_to)}</span>
          </p>
        )}
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <svg className="animate-spin h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <SummaryCard icon={DollarSign} label="Total Expenditure"   value={fmt(data.summary.total_cost)} color="bg-green-50 text-green-600" />
            <SummaryCard icon={Users}      label="Active Employees"    value={data.per_tool.reduce((sum, t) => sum + Number(t.current_active_users), 0)} color="bg-blue-50 text-blue-600" />
            <SummaryCard icon={Wrench}     label="Tools Used"          value={data.summary.tools_used} color="bg-purple-50 text-purple-600" />
            <SummaryCard icon={BarChart2}  label="Billed Periods"      value={data.per_tool.reduce((sum, t) => sum + Number(t.period_allocations), 0)} color="bg-orange-50 text-orange-600" />
          </div>

          {/* Paid By split */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="card p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-50 text-emerald-600">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{fmt(data.summary.bacancy_cost || 0)}</p>
                <p className="text-sm text-gray-500">Paid by Bacancy</p>
              </div>
            </div>
            <div className="card p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-50 text-blue-600">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{fmt(data.summary.client_cost || 0)}</p>
                <p className="text-sm text-gray-500">Paid by Client</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Per-Tool cost */}
            <div className="card p-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Cost per AI Tool</h2>
              {data.per_tool.length === 0 ? (
                <p className="text-sm text-gray-400">No data for the selected filters.</p>
              ) : (
                <div className="space-y-4">
                  {data.per_tool.map((t) => (
                    <div key={t.id}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Wrench className="w-3.5 h-3.5 text-primary-500" />
                          <span className="text-sm font-medium text-gray-800">{t.tool_name}</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            {t.current_active_users} active
                          </span>
                          {Number(t.period_allocations) > Number(t.current_active_users) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                              +{Number(t.period_allocations) - Number(t.current_active_users)} migrated
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-bold text-gray-900">{fmt(t.total_cost)}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all duration-500"
                          style={{ width: `${(Number(t.total_cost) / maxToolCost) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{fmt(t.monthly_price)}/mo · cost includes migrated periods</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Per-Dept cost */}
            <div className="card p-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Cost by Department</h2>
              {data.per_department.length === 0 ? (
                <p className="text-sm text-gray-400">No data for the selected filters.</p>
              ) : (
                <div className="space-y-4">
                  {data.per_department.map((d) => (
                    <div key={d.department}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                            {d.department}
                          </span>
                          <span className="text-xs text-gray-400">{d.employee_count} employee{d.employee_count !== '1' ? 's' : ''}</span>
                        </div>
                        <span className="text-sm font-bold text-gray-900">{fmt(d.total_cost)}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-purple-400 rounded-full transition-all duration-500"
                          style={{ width: `${(Number(d.total_cost) / maxDeptCost) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Paid By breakdown */}
          {data.per_paid_by && data.per_paid_by.length > 0 && (
            <div className="card p-6 mt-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Cost by Paid By</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Paid By</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500">Employees</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500">Total Cost</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.per_paid_by.map((p) => (
                      <tr key={p.paid_by} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            p.paid_by === 'Client' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
                          }`}>
                            {p.paid_by}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">{p.employee_count}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(p.total_cost)}</td>
                        <td className="px-4 py-3 text-right text-gray-500">
                          {data.summary.total_cost > 0
                            ? `${((Number(p.total_cost) / Number(data.summary.total_cost)) * 100).toFixed(1)}%`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Detailed table */}
          <div className="card mt-6">
            <div className="px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-sm font-semibold text-gray-700">Detailed Breakdown — Per Tool</h2>
                <p className="text-xs text-gray-400 mt-0.5">Active Users = currently on this tool · Billed Periods = all allocation periods in range (incl. migrations)</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-6 py-3 font-medium text-gray-500">AI Tool</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Monthly Price</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Active Users</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Billed Periods</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Total Cost</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">% of Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.per_tool.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-400 text-sm">No data available.</td>
                    </tr>
                  ) : (
                    <>
                      {data.per_tool.map((t) => (
                        <tr key={t.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 font-medium text-gray-900">{t.tool_name}</td>
                          <td className="px-6 py-4 text-right text-gray-600">{fmt(t.monthly_price)}</td>
                          <td className="px-6 py-4 text-right">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                              {t.current_active_users}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-gray-500">{t.period_allocations}</td>
                          <td className="px-6 py-4 text-right font-semibold text-gray-900">{fmt(t.total_cost)}</td>
                          <td className="px-6 py-4 text-right text-gray-500">
                            {data.summary.total_cost > 0
                              ? `${((Number(t.total_cost) / Number(data.summary.total_cost)) * 100).toFixed(1)}%`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                        <td className="px-6 py-3 text-gray-700">Total</td>
                        <td className="px-6 py-3 text-right text-gray-500">—</td>
                        <td className="px-6 py-3 text-right text-gray-700">
                          {data.per_tool.reduce((sum, t) => sum + Number(t.current_active_users), 0)}
                        </td>
                        <td className="px-6 py-3 text-right text-gray-700">
                          {data.per_tool.reduce((sum, t) => sum + Number(t.period_allocations), 0)}
                        </td>
                        <td className="px-6 py-3 text-right text-green-700 text-base">{fmt(data.summary.total_cost)}</td>
                        <td className="px-6 py-3 text-right text-gray-500">100%</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CostReport;
